import { Fragment } from "react";
import {
  DateFilterState,
  Filter,
  FilterConfiguration,
  NumberFilterState,
} from "./types";
import styles from "./FilterPreview.module.scss";
import Button from "../Button/Button";
import { Trash } from "../Icon/Icon";
import toTitleCase from "../../util/toTitleCase";
import dateStringLabels from "./ConfigureFilter/date-string-labels";

function FilterPreviewContainer({
  children,
  onEdit,
  onDelete,
}: {
  children: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={styles.preview}>
      <Button
        className={styles.editButton}
        variant="unstyled"
        onClick={onEdit}
      />
      <p>{children}</p>
      <Button
        variant="unstyled"
        className={styles.deleteFilterButton}
        onClick={onDelete}
      >
        <Trash size="medium" />
      </Button>
    </div>
  );
}

function FilterPreview({
  filter,
  onEdit,
  onDelete,
}: {
  filter: Filter;
  onEdit: (filter: FilterConfiguration) => void;
  onDelete: () => void;
}) {
  if (filter.type === "enum") {
    return (
      <FilterPreviewContainer
        onDelete={onDelete}
        onEdit={() =>
          onEdit({
            type: "enum",
            name: filter.configuration.name,
            label: filter.configuration.label,
            values: filter.configuration.values,
            initialState: {
              filterType: filter.filterType,
              values: filter.values,
            },
          })
        }
      >
        <span
          className={styles.previewField}
        >{`${filter.configuration.label} `}</span>
        {filter.values.length && (
          <>
            <span>{`is ${
              filter.filterType === "IS_ANY_OF" ? "any" : "none"
            } of `}</span>
            {filter.values.map((value, index) => (
              <Fragment key={value.value}>
                <span className={styles.previewValue}>
                  {toTitleCase(value.label)}
                </span>
                {index !== filter.values.length - 1 && " or "}
              </Fragment>
            ))}
          </>
        )}
      </FilterPreviewContainer>
    );
  }

  if (filter.type === "string") {
    return (
      <FilterPreviewContainer
        onDelete={onDelete}
        onEdit={() =>
          onEdit({
            type: "string",
            name: filter.configuration.name,
            label: filter.configuration.label,
            initialState: {
              filterType: filter.filterType,
              value: filter.value,
            },
          })
        }
      >
        <span
          className={styles.previewField}
        >{`${filter.configuration.label} `}</span>
        <span>
          {`${
            filter.filterType === "CONTAINS" ? "contains" : "doesn't contain"
          } `}
        </span>
        <span className={styles.previewValue}>{filter.value}</span>
      </FilterPreviewContainer>
    );
  }

  if (filter.type === "number") {
    return (
      <FilterPreviewContainer
        onDelete={onDelete}
        onEdit={() =>
          onEdit({
            type: "number",
            name: filter.configuration.name,
            label: filter.configuration.label,
            initialState: {
              filterType: filter.filterType,
              value: filter.value,
            } as NumberFilterState,
          })
        }
      >
        {filter.filterType === "EQUALS" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`equals `}</span>
            <span className={styles.previewValue}>
              {Intl.NumberFormat().format(filter.value)}
            </span>
          </>
        )}
        {filter.filterType === "GREATER_THAN" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is greater than `}</span>
            <span className={styles.previewValue}>
              {Intl.NumberFormat().format(filter.value)}
            </span>
          </>
        )}
        {filter.filterType === "LESS_THAN" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is less than `}</span>
            <span className={styles.previewValue}>
              {Intl.NumberFormat().format(filter.value)}
            </span>
          </>
        )}
        {filter.filterType === "BETWEEN" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is between `}</span>
            <span className={styles.previewValue}>
              {Intl.NumberFormat().format(filter.value.from)}
            </span>
            <span>{` and `}</span>
            <span className={styles.previewValue}>
              {Intl.NumberFormat().format(filter.value.to)}
            </span>
          </>
        )}
      </FilterPreviewContainer>
    );
  }

  if (filter.type === "date") {
    return (
      <FilterPreviewContainer
        onDelete={onDelete}
        onEdit={() =>
          onEdit({
            type: "date",
            name: filter.configuration.name,
            label: filter.configuration.label,
            initialState: {
              filterType: filter.filterType,
              value: filter.value,
            } as DateFilterState,
          })
        }
      >
        {filter.filterType === "IS" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is `}</span>
            <span className={styles.previewValue}>
              {dateStringLabels[filter.value]}
            </span>
          </>
        )}
        {filter.filterType === "EQUALS" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is `}</span>
            <span className={styles.previewValue}>
              {new Date(filter.value).toLocaleDateString()}
            </span>
          </>
        )}
        {filter.filterType === "BEFORE" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is before `}</span>
            <span className={styles.previewValue}>
              {new Date(filter.value).toLocaleDateString()}
            </span>
          </>
        )}
        {filter.filterType === "AFTER" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is after `}</span>
            <span className={styles.previewValue}>
              {new Date(filter.value).toLocaleDateString()}
            </span>
          </>
        )}
        {filter.filterType === "BETWEEN" && (
          <>
            <span
              className={styles.previewField}
            >{`${filter.configuration.label} `}</span>
            <span>{`is between `}</span>
            <span className={styles.previewValue}>
              {new Date(filter.value.from).toLocaleDateString()}
            </span>
            <span>{` and `}</span>
            <span className={styles.previewValue}>
              {new Date(filter.value.to).toLocaleDateString()}
            </span>
          </>
        )}
      </FilterPreviewContainer>
    );
  }
}

export default FilterPreview;
